import React, { useState, useEffect, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import Chat from './Chat';
import { useClientChat } from './hooks';
import { fetchAPI } from '../../ducks/api';
import BroadcastMedia, { broadcastText } from '../BroadcastMedia';
import './ChatWidget.css';

// localStorage key for the "last time the user saw the Broadcasts
// tab inside the chat bubble" timestamp. Drives the unread-count
// badge on the tab + the bubble icon. Per-device by design — read
// receipts are intentionally not persisted server-side for v1.
const BROADCASTS_SEEN_KEY = 'broadcasts:lastSeenAt';

function readLastSeenAt() {
  try {
    const v = localStorage.getItem(BROADCASTS_SEEN_KEY);
    return v ? Number(v) : 0;
  } catch (_) {
    return 0;
  }
}

function writeLastSeenAt(ts) {
  try {
    localStorage.setItem(BROADCASTS_SEEN_KEY, String(ts));
  } catch (_) {
    // private-browsing → no-op
  }
}

/**
 * ClientFloatingChatWidget — bubble for end-user clients to chat with their
 * trainer. Owns only the bubble chrome (open/close, position, drag, ESC).
 * Conversation orchestration lives in `useClientChat`.
 */
function ClientFloatingChatWidget({ currentUser }) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState(() => {
    const isMobile = window.innerWidth < 768;
    return {
      x: isMobile ? 20 : window.innerWidth - 420,
      y: window.innerHeight - 620,
    };
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const widgetRef = useRef(null);

  const [dimensions, setDimensions] = useState(() => {
    const isMobile = window.innerWidth < 768;
    return {
      width: isMobile ? window.innerWidth - 40 : 400,
      height: isMobile ? window.innerHeight - 100 : 600,
    };
  });

  const {
    conversation,
    loading,
    unreadCount,
    loadOrCreateConversation,
    markRead,
    trainerName,
  } = useClientChat({ currentUser });

  // ----- Broadcasts inside the bubble ----------------------------------
  // The full /dashboard/messages?tab=broadcasts page is the canonical
  // broadcasts surface. The bubble surfaces them too because clients
  // primarily live in the bubble — without an in-bubble tab they
  // miss announcements until they happen to navigate to the full
  // page. Tab UI here mirrors the full page minus the input bar.
  const [topTab, setTopTab] = useState('conversations');
  const [broadcasts, setBroadcasts] = useState([]);
  const [broadcastsLoading, setBroadcastsLoading] = useState(false);
  const [lastSeenBroadcastsAt, setLastSeenBroadcastsAt] = useState(
    () => readLastSeenAt()
  );

  // Fetch broadcasts whenever the bubble opens (cheap call, fire on
  // open rather than on every render). Re-fetch when the tab is
  // switched to broadcasts so a fresh announcement that lands while
  // the bubble's open still appears.
  const refreshBroadcasts = useCallback(async () => {
    if (!currentUser) return;
    try {
      setBroadcastsLoading(true);
      const data = await fetchAPI('/broadcasts/me?limit=50');
      setBroadcasts(Array.isArray(data) ? data : []);
    } catch (_) {
      setBroadcasts([]);
    } finally {
      setBroadcastsLoading(false);
    }
  }, [currentUser]);

  // Count broadcasts newer than the last time the user actually
  // viewed the Broadcasts tab. Drives the badge on both the tab and
  // the closed-bubble icon.
  const unreadBroadcasts = broadcasts.reduce((n, b) => {
    const t = b?.sentAt ? Date.parse(b.sentAt) : 0;
    return t > lastSeenBroadcastsAt ? n + 1 : n;
  }, 0);

  // When the user flips to the Broadcasts tab, treat that as "seen".
  const handleSelectTab = (tab) => {
    setTopTab(tab);
    if (tab === 'broadcasts') {
      const now = Date.now();
      writeLastSeenAt(now);
      setLastSeenBroadcastsAt(now);
    }
  };

  // Responsive resize: keep the bubble sized + parked in the bottom-right.
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 768;
      const newDimensions = {
        width: isMobile ? window.innerWidth - 40 : 400,
        height: isMobile ? window.innerHeight - 100 : 600,
      };
      setDimensions(newDimensions);
      setPosition({
        x: isMobile ? 20 : window.innerWidth - newDimensions.width - 20,
        y: window.innerHeight - newDimensions.height - 20,
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /* ----- drag chrome ----- */
  const handleMouseDown = (e) => {
    if (e.target.tagName === 'BUTTON') return;
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging) return;
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;
      const boundedX = Math.max(
        0,
        Math.min(newX, window.innerWidth - dimensions.width)
      );
      const boundedY = Math.max(
        0,
        Math.min(newY, window.innerHeight - dimensions.height)
      );
      setPosition({ x: boundedX, y: boundedY });
    },
    [isDragging, dragStart, dimensions]
  );

  const handleMouseUp = useCallback(() => setIsDragging(false), []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart, handleMouseMove, handleMouseUp]);

  // ESC closes the widget.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) setIsOpen(false);
    };
    if (isOpen) document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleOpenChat = async () => {
    setIsOpen(true);
    markRead();
    refreshBroadcasts();
  };

  // Combined badge on the closed bubble icon: chat unreads + broadcast
  // unreads. Broadcasts are the only proactive signal a client gets
  // about coach announcements on web, so without including them the
  // icon would silently hide new announcements behind a closed bubble.
  const totalUnread = (unreadCount || 0) + unreadBroadcasts;

  if (!currentUser) return null;

  return (
    <>
      {!isOpen && (
        <div style={{ position: 'relative' }}>
          <button
            className='floating-chat-button'
            onClick={handleOpenChat}
            aria-label='Open chat with admin'
            style={{ position: 'relative' }}
          >
            <svg width='24' height='24' fill='white' viewBox='0 0 24 24'>
              <path d='M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z' />
            </svg>
            {totalUnread > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '-8px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  border: '2px solid white',
                }}
              >
                {totalUnread > 99 ? '99+' : totalUnread}
              </span>
            )}
          </button>
        </div>
      )}

      {isOpen && (
        <div
          ref={widgetRef}
          className={`floating-chat-widget ${isDragging ? 'dragging' : ''} ${
            window.innerWidth < 768 ? 'mobile-chat-widget' : ''
          }`}
          style={{
            left: window.innerWidth >= 768 ? `${position.x}px` : '0',
            top: window.innerWidth >= 768 ? `${position.y}px` : '0',
            width: window.innerWidth >= 768 ? `${dimensions.width}px` : '100vw',
            height:
              window.innerWidth >= 768 ? `${dimensions.height}px` : '100vh',
          }}
        >
          <div
            className='floating-chat-header draggable'
            onMouseDown={handleMouseDown}
          >
            <div className='header-left'>
              <span className='header-title'>Chat with {trainerName}</span>
            </div>
            <button onClick={() => setIsOpen(false)} className='close-button'>
              ×
            </button>
          </div>

          {/* Two-tab strip — Conversations vs. Broadcasts. The chat
              UI below swaps based on the selected tab. */}
          <div className='floating-chat-tabs' role='tablist'>
            <button
              type='button'
              role='tab'
              aria-selected={topTab === 'conversations'}
              className={`fct-tab ${topTab === 'conversations' ? 'is-active' : ''}`}
              onClick={() => handleSelectTab('conversations')}
            >
              Conversations
            </button>
            <button
              type='button'
              role='tab'
              aria-selected={topTab === 'broadcasts'}
              className={`fct-tab ${topTab === 'broadcasts' ? 'is-active' : ''}`}
              onClick={() => handleSelectTab('broadcasts')}
            >
              Broadcasts
              {unreadBroadcasts > 0 && topTab !== 'broadcasts' ? (
                <span className='fct-tab-badge'>
                  {unreadBroadcasts > 99 ? '99+' : unreadBroadcasts}
                </span>
              ) : null}
            </button>
          </div>

          <div className='chat-content'>
            {topTab === 'broadcasts' ? (
              <BroadcastsBubbleList
                items={broadcasts}
                loading={broadcastsLoading}
              />
            ) : loading && !conversation ? (
              <div className='chat-loading-state'>Loading conversation...</div>
            ) : conversation ? (
              <Chat
                conversationId={conversation._id}
                currentUser={{
                  id: currentUser.uid,
                  name:
                    currentUser.name ||
                    `${currentUser.name || ''} ${currentUser.lastName || ''}`.trim(),
                }}
              />
            ) : (
              <div className='chat-error-state'>
                <div style={{ marginBottom: '16px' }}>
                  Unable to load conversation
                </div>
                <button
                  onClick={loadOrCreateConversation}
                  className='retry-button'
                >
                  Retry
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Read-only list of broadcasts shown inside the chat bubble.
 * Same data shape as the full /dashboard/messages?tab=broadcasts
 * page, but tighter padding to fit in the 400px-wide bubble.
 */
function BroadcastsBubbleList({ items, loading }) {
  if (loading && items.length === 0) {
    return <div className='chat-loading-state'>Loading broadcasts...</div>;
  }
  if (items.length === 0) {
    return (
      <div className='broadcasts-bubble-empty'>
        <div className='broadcasts-bubble-empty-title'>No broadcasts yet</div>
        <div className='broadcasts-bubble-empty-sub'>
          Coach announcements will show up here. They&apos;re one-way — no need
          to reply.
        </div>
      </div>
    );
  }
  return (
    <div className='broadcasts-bubble-list'>
      {items.map((b) => {
        const trainerName =
          [b.trainer?.name, b.trainer?.lastName].filter(Boolean).join(' ').trim() ||
          'Your coach';
        return (
          <div key={b._id} className='broadcasts-bubble-card'>
            <div className='broadcasts-bubble-card-meta'>
              <span className='broadcasts-bubble-sender'>{trainerName}</span>
              {b.sentAt ? (
                <span className='broadcasts-bubble-when'>
                  {moment(b.sentAt).fromNow()}
                </span>
              ) : null}
            </div>
            {b.title ? (
              <div className='broadcasts-bubble-title'>{b.title}</div>
            ) : null}
            {broadcastText(b) ? (
              <div className='broadcasts-bubble-body'>{broadcastText(b)}</div>
            ) : null}
            <BroadcastMedia broadcast={b} compact />
          </div>
        );
      })}
    </div>
  );
}

const mapStateToProps = (state) => ({
  currentUser: state.auth.user,
});

export default connect(mapStateToProps)(ClientFloatingChatWidget);
